import axios from 'axios';
import { OUTGOING_HEADER_NAMES } from '@/consts/headers';
import { log } from '@/logger/logger';
import { captureCredentialSession, getAxiosInstance, isCredentialSessionCurrent } from '@/services/axiosConfig';
import { getIdTokenWithDeadline } from '@/services/auth/token-refresh';

const axiosInstance = getAxiosInstance();
const logger = log.getLogger('auth-headers');

interface AuthTokenProvider {
  getIdToken(forceRefresh?: boolean): Promise<string | null | undefined>;
}

/**
 * Clears the default Authorization header from both the global axios default and the
 * shared axios instance. Used on sign-out and whenever there is no token to send.
 */
export const unsetDefaultHeaders = () => {
  delete axios.defaults.headers.Authorization;
  delete axiosInstance.defaults.headers.Authorization;
  delete axios.defaults.headers.common.Authorization;
  delete axiosInstance.defaults.headers.common.Authorization;
};

/**
 * Sets the default `Bearer` Authorization header on both axios clients.
 *
 * An authenticated-but-tokenless caller (e.g. a logged-out "Anyone with the link" embed
 * viewer) has no token to send. Writing `Bearer ${accessToken}` would coerce the missing
 * value into the literal header `Bearer undefined`, which the server logs as an
 * "Invalid Figma JWT". When the token is absent we clear the header instead so the request
 * takes the clean unauthorized path rather than sending a bogus token.
 */
export const setDefaultHeaders = (accessToken?: string) => {
  if (!accessToken) {
    unsetDefaultHeaders();
    return;
  }
  axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
};

/**
 * Force-refreshes the provider token and updates both axios clients, unless a logout supersedes the
 * session mid-refresh (then the departed token is dropped rather than installed).
 *
 * When a known-valid token is supplied, a transient refresh failure keeps that token in
 * place so authentication can continue. Without a fallback, the failure remains observable
 * to the caller.
 */
export const refreshDefaultHeaders = async (provider: AuthTokenProvider, fallbackToken?: string): Promise<void> => {
  const credentialSession = captureCredentialSession();
  let tokenToInstall: string | undefined;
  try {
    // A hanging refresh (not just a rejecting one) must fall back too, so bound it — otherwise sign-in
    // stalls here, before enrichUser, with the full-screen loader up.
    const refreshedToken = await getIdTokenWithDeadline(provider, true);
    tokenToInstall = refreshedToken ?? fallbackToken;
  } catch (error) {
    if (fallbackToken === undefined) {
      throw error;
    }
    logger.warn('Failed to refresh auth token; using the existing token', error);
    tokenToInstall = fallbackToken;
  }
  // A logout (or logout→sign-in) during the up-to-10s refresh makes the token the departed session's;
  // installing it into the shared defaults would authenticate later requests as them.
  if (!isCredentialSessionCurrent(credentialSession)) {
    return;
  }
  setDefaultHeaders(tokenToInstall);
};

/** Provider and version metadata share the same defaults as authentication. Null clears optional-auth metadata. */
export const setProviderHeaders = (provider: string | null, appVersion: string | undefined) => {
  for (const headers of [axios.defaults.headers.common, axiosInstance.defaults.headers.common]) {
    if (provider === null) {
      delete headers['X-Weavy-Auth-Provider'];
    } else {
      headers['X-Weavy-Auth-Provider'] = provider;
    }
    headers[OUTGOING_HEADER_NAMES.APP_VERSION] = appVersion;
  }
};
